/**
 * useDocumentKeyDown — Subscribe to `keydown` on `window`.
 *
 * Generalizes the same-named hook several demos had open-coded. The
 * handler receives the raw `KeyboardEvent` so callers can branch on
 * `event.key`, modifier keys, repeat state, target type, etc.
 *
 * Skips events whose default has already been prevented (so a Radix
 * dialog, command palette, or focus-trap can claim the key before the
 * page-level listener runs) and short-circuits when `enabled` is
 * `false` without unmounting the component.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useEffect } from "react";
import { isEditableElementTarget } from "../utils/editableTarget.js";
import { useStableCallback } from "./useStableCallback.js";

export interface UseDocumentKeyDownOptions {
  /** Toggle the listener without unmounting the consumer. */
  readonly enabled?: boolean;
  /**
   * Skip events whose target is an `<input>`, `<textarea>`, `<select>`,
   * or `contentEditable` element. Defaults to `true` so a global "go
   * to home" hotkey does not fire while the user is typing into a
   * search box or keyboard-navigating a select. `<select>` is included
   * because native selects steer arrow keys to option navigation, and
   * a global Up/Down hotkey would conflict with that interaction.
   */
  readonly skipWhileTyping?: boolean;
}

export function useDocumentKeyDown(
  handler: (event: KeyboardEvent) => void,
  options: UseDocumentKeyDownOptions = {},
): void {
  const { enabled = true, skipWhileTyping = true } = options;
  const stableHandler = useStableCallback(handler);
  useEffect(() => {
    if (!enabled) return undefined;
    const listener = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (skipWhileTyping && isEditableElementTarget(event.target)) return;
      stableHandler(event);
    };
    window.addEventListener("keydown", listener);
    return (): void => {
      window.removeEventListener("keydown", listener);
    };
  }, [enabled, skipWhileTyping, stableHandler]);
}
